import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';

/**
 * Hook for fetching admin metrics data from the consolidated /api/metrics endpoint
 * @param {Object} options
 * @param {number} options.windowMinutes - Time window in minutes (default: 60)
 * @param {boolean} options.autoRefresh - Whether to auto-refresh (default: false)
 * @param {number} options.refreshInterval - Auto-refresh interval in ms (default: 30000)
 */
export const useAdminMetrics = (options = {}) => {
  const {
    windowMinutes = 60,
    autoRefresh = false,
    refreshInterval = 30000,
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: responseData } = await axios.get(`${API_BASE_URL}/metrics`, {
        params: { windowMinutes },
        withCredentials: true,
      });

      const { metrics } = responseData;

      setData({
        summary: {
          timestamp: metrics.timestamp,
          uptimeMinutes: metrics.uptimeMinutes,
          metricsWindowMinutes: metrics.metricsWindowMinutes,
        },
        responseTime: metrics.responseTime,
        externalApis: metrics.externalApis,
        dynamoDb: metrics.dynamoDb,
      });
    } catch (err) {
      console.error('Error fetching admin metrics:', err);
      setError(err.response?.data?.error || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [windowMinutes]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const resetMetrics = useCallback(async () => {
    try {
      await axios.post(`${API_BASE_URL}/metrics/reset`, {}, {
        withCredentials: true,
      });
      await fetchData();
    } catch (err) {
      console.error('Error resetting metrics:', err);
      setError(err.response?.data?.error || 'Failed to reset metrics');
    }
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchData, refreshInterval);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, fetchData]);

  return { data, loading, error, refresh, resetMetrics };
};

export default useAdminMetrics;
